import type { GeoPayload } from './types'

/** Only the two collections are needed; the rest of GeoPayload is irrelevant here. */
type Geometry = Pick<GeoPayload, 'geofences' | 'routes'>

/**
 * The corridor's name, derived from the published geometry rather than typed into a header.
 *
 * Two things make this less trivial than picking the first and last hub.
 *
 * NOT EVERY WAYPOINT IS A PLACE. A route's hub list is the full ordered sequence of
 * geofences it passes through, which on this corridor means gates, tarpaulin bays and
 * approach queues alongside the actual plants and depots. Taking the middle element of
 * that list is what produced "DHAR -> DHULE-Q -> DHULE" -- a gate queue promoted to the
 * status of a city because it happened to sit at index four.
 *
 * ONE PLACE CAN BE SEVERAL FENCES. Dhule is drawn as a queue, a gate, a yard-in and a
 * dwell yard: four fences, metres apart, one destination. Deduping by hub_code keeps all
 * four; deduping by GEOGRAPHY keeps one. Proximity is the honest test because it stays
 * correct when somebody adds a fifth fence at Dhule tomorrow, whereas any rule based on
 * the shape of the code strings ("DHULE-YARD-IN starts with DHULE") breaks the first time
 * a hub is named after the village rather than the customer.
 */

/** Fence types that are a destination rather than something passed on the way to one. */
const PLACE_TYPES = new Set(['plant', 'depot', 'yard', 'hub'])

/** Fences closer than this to each other are the same place. */
const SAME_PLACE_KM = 4

export interface CorridorPlace {
  code: string
  lat: number
  lon: number
}

export function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371
  const dLat = ((bLat - aLat) * Math.PI) / 180
  const dLon = ((bLon - aLon) * Math.PI) / 180
  const p = Math.sin(dLat / 2) ** 2
    + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(p)))
}

/**
 * The ordered destinations along a route: hub-type fences only, collapsed by proximity.
 *
 * Each group is labelled with its SHORTEST hub code, which is reliably the parent -- the
 * dwell yard is DHULE and its gate is DHULE-IN, never the reverse.
 */
export function corridorPlaces(geo: Geometry | null | undefined): CorridorPlace[] {
  const route = geo?.routes?.features?.[0]
  const hubs = route?.properties?.hubs
  if (!Array.isArray(hubs) || !geo?.geofences) return []

  const byId = new Map(
    geo.geofences.features.map((f) => [f.properties.geofence_id, f.properties]),
  )

  const stops: CorridorPlace[] = []
  for (const h of hubs) {
    const ref = h as unknown as { hub_code?: string; geofence_id?: string }
    const code = typeof h === 'string' ? h : ref.hub_code
    if (!code) continue
    const f = ref.geofence_id ? byId.get(ref.geofence_id) : undefined
    // No matching fence means the route references geometry we were never sent. Dropping
    // it is right: an unresolvable waypoint cannot be confirmed to be a destination, and
    // guessing would put the gate queues back in the header.
    if (!f || !PLACE_TYPES.has(String(f.type).toLowerCase())) continue
    if (f.centroid_lat == null || f.centroid_lon == null) continue
    stops.push({ code, lat: f.centroid_lat, lon: f.centroid_lon })
  }

  // Collapse by proximity against every group already seen, not just the previous one:
  // the empty leg revisits Dhar at both ends of its sequence, and a consecutive-only
  // check would emit it twice.
  const places: CorridorPlace[] = []
  for (const s of stops) {
    const near = places.find((p) => haversineKm(p.lat, p.lon, s.lat, s.lon) <= SAME_PLACE_KM)
    if (!near) {
      places.push(s)
    } else if (s.code.length < near.code.length) {
      near.code = s.code            // the shortest code is the parent
    }
  }
  return places
}

/**
 * "DHAR → JHUL → DHULE".
 *
 * Elides the middle when there are many stops rather than truncating the end: the two
 * endpoints are what name a corridor, and a header that reads "DHAR → JHUL → …" has lost
 * the thing it exists to say.
 */
export function corridorLabel(geo: Geometry | null | undefined, maxStops = 4): string {
  const places = corridorPlaces(geo)
  if (places.length >= 2) {
    const codes = places.map((p) => p.code)
    if (codes.length <= maxStops) return codes.join(' → ')
    return [codes[0], '…', codes[codes.length - 1]].join(' → ')
  }
  return geo?.routes?.features?.[0]?.properties?.name ?? 'corridor not published'
}
